import type { ChatStreamRequest } from '@llura/types';

/**
 * Stream the assistant's next turn from the API.
 *
 * POSTs the full conversation + coach + covenant + locale to
 * `POST /api/chat/stream`, then yields text deltas as they arrive.
 *
 * Uses `fetch` + a `ReadableStream` reader (not `EventSource`) because
 * EventSource is GET-only and we need a JSON body large enough to hold the
 * whole conversation history.
 *
 * Server-Sent Events on the wire:
 *   id: 1
 *   data: Hey
 *
 *   id: 2
 *   data: ,
 *
 *   event: done
 *   id: 3
 *   data:
 *
 * A blank line terminates each event. `event: done` ends the stream cleanly.
 * `event: error` raises.
 */
export async function* streamChat(
  request: ChatStreamRequest,
  signal?: AbortSignal,
): AsyncIterable<string> {
  const res = await fetch('/api/chat/stream', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
    signal,
  });

  if (!res.ok || !res.body) {
    throw new Error(`Chat stream failed: ${res.status} ${res.statusText}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) return;
      buffer += decoder.decode(value, { stream: true });
      // Tolerate \r\n line endings from intermediaries (Caddy, proxies).
      buffer = buffer.replace(/\r\n/g, '\n');

      let boundary: number;
      while ((boundary = buffer.indexOf('\n\n')) !== -1) {
        const eventBlock = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);

        let event = 'message';
        const dataLines: string[] = [];
        for (const line of eventBlock.split('\n')) {
          if (line.startsWith('event: ')) event = line.slice(7);
          else if (line.startsWith('data: ')) dataLines.push(line.slice(6));
          else if (line === 'data:') dataLines.push('');
        }

        if (event === 'done') return;
        if (event === 'error') {
          throw new Error(dataLines.join('\n') || 'Chat stream error');
        }
        if (dataLines.length > 0) yield dataLines.join('\n');
      }
    }
  } finally {
    reader.cancel().catch(() => undefined);
  }
}
